
import { useEffect, useState } from 'react'

import ConfirmModal from 'common/ConfirmModal'
import { FilledBlue } from 'css/Buttons'
import store from 'redux/store'
import { setCrmUser } from 'redux/actions'
import { getCrmUser } from 'redux/selectors'

import { getBrochurePreSignedUrl, updateCurrentCeirYear } from '../../reports/documentsapi'
import BrochureUploadButton from './BrochureUploadButton'
import confirmModalCss from '../sass/confirm.modal.module.scss'
import css from '../sass/upload.documents.module.scss'

export const fullName = "CEIR Fund Reports"
export const uploadTitle = "CEIR (Giving in Action) Fund Reports"

export const ReportContent = () => {
  const crmUser = getCrmUser(store.getState())
  const currentCeirYear = crmUser.settings.currentCeirYear
  const ceirYearUpdated = new Date(crmUser.settings.ceirYearUpdated)
  const sixMonthsAgo = new Date()
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6)
  const isSeleniumTest = localStorage.isSeleniumTest === 'true'
  const hideIncrementYear = !isSeleniumTest && !Number.isNaN(ceirYearUpdated.getTime()) && ceirYearUpdated > sixMonthsAgo
  const [showIncrementYearConfirmModal, setShowIncrementYearConfirmModal] = useState(false)
  const [displayedCurrentCeirYear, setDisplayedCurrentCeirYear] = useState(currentCeirYear)
  const [updateYearError, setUpdateYearError] = useState('')
  const [isSavingYear, setIsSavingYear] = useState(false)
  const [hasCurrentCeirBrochure, setHasCurrentCeirBrochure] = useState(null)
  const nextCeirYear = Number(displayedCurrentCeirYear) + 1

  useEffect(() => {
    let isCurrentRequest = true

    setHasCurrentCeirBrochure(null)
    getBrochurePreSignedUrl('CEIR', displayedCurrentCeirYear)
      .then(response => {
        if (!isCurrentRequest) return

        if (response?.presignedUrl)
          setHasCurrentCeirBrochure(true)
        else if (response?.error === 'BROCHURE_NOT_FOUND')
          setHasCurrentCeirBrochure(false)
      })
      .catch(() => {})

    return () => {
      isCurrentRequest = false
    }
  }, [displayedCurrentCeirYear])

  const saveCurrentCeirYear = async () => {
    if (isSavingYear) return

    setIsSavingYear(true)
    setUpdateYearError('')

    try {
      const response = await updateCurrentCeirYear(nextCeirYear)
      if (!response?.success) {
        const responseError = typeof response?.error === 'string'
          ? response.error
          : response?.error?.message || response?.error?.msg || response?.message

        setUpdateYearError(responseError || 'There was an error updating the Current CEIR Year.')
        return
      }

      setDisplayedCurrentCeirYear(response.currentCeirYear)
      store.dispatch(setCrmUser({
        ...crmUser,
        settings: {
          ...crmUser.settings,
          currentCeirYear: response.currentCeirYear,
          ceirYearUpdated: response.ceirYearUpdated,
        },
      }))
    }
    catch (error) {
      setUpdateYearError(error.message || 'There was an error updating the Current CEIR Year.')
    }
    finally {
      setIsSavingYear(false)
      setShowIncrementYearConfirmModal(false)
    }
  }

  return (
    <>
      <div className={css.ceirYearControls}>
        <span>Current CEIR Year:</span>
        <strong className={css.currentCeirYear}>{displayedCurrentCeirYear}</strong>
        {!hideIncrementYear &&
          <div className={css.ceirYearAction}>
            <FilledBlue
              type="button"
              isSmall
              height={2.75}
              className={css.reportUploadControlButton}
              onClick={() => {
                setUpdateYearError('')
                setShowIncrementYearConfirmModal(true)
              }}
            >
              Increment Current CEIR Year
            </FilledBlue>
            {updateYearError && <span className={css.ceirYearError}>{updateYearError}</span>}
          </div>}
        <div className={`${css.brochureAction}${!hideIncrementYear ? ` ${css.ceirBrochureButton}` : ''}`}>
          <BrochureUploadButton
            year={hideIncrementYear ? displayedCurrentCeirYear : nextCeirYear}
            type="CEIR"
            onUploadSuccess={({year}) => {
              if (String(year) === String(displayedCurrentCeirYear))
                setHasCurrentCeirBrochure(true)
            }}
          />
          {hasCurrentCeirBrochure === false &&
            <span className={css.brochureWarning}>
              WARNING: No Giving in Action brochure has been uploaded yet for {displayedCurrentCeirYear}.
            </span>}
        </div>
      </div>

      {showIncrementYearConfirmModal &&
        <ConfirmModal
          onConfirm={saveCurrentCeirYear}
          onCancel={() => setShowIncrementYearConfirmModal(false)}
          cancelText="CANCEL"
          confirmText={isSavingYear ? 'SAVING...' : 'SAVE YEAR'}
        >
          <div className={confirmModalCss.modalInner}>
            <div className={`${confirmModalCss.modalText} ${css.ceirYearModalText}`}>
              Are you sure you want to increment the Current CEIR Year from <strong>{displayedCurrentCeirYear}</strong> to <strong>{nextCeirYear}</strong>?
              {' '}Remember that CEIR year tends to lag the actual current year by a year and a half or more.
            </div>
          </div>
        </ConfirmModal>}

      <p><b>All files should be named in the following format:</b>
      <br/>[fund number].CEIR.[year of report].[file extenstion]</p>

      <div>
        <b>For example:</b>
        <ol>
          <li><b>PDF:</b> 31588C - GZ.CEIR.2021.pdf</li>
          <li><b>Movie:</b> 50029 - BE.CEIR.2021.mp4</li>
        </ol>
        <br/>
      </div>
    </>
  )
}

export const validateFile = (file, errors) => {
  const [fundNumber, reportType, reportYear, fileExt, badData] = file.name.split('.');

  if (badData) {
    errors.push({file, msg: "Filename has too many sections"})
  }
  else if (!fileExt) {
    errors.push({file, msg: "Filename has too few sections"})
  }
  else if (reportType.toUpperCase() !== 'CEIR') {
    errors.push({file, msg: "Only CEIR reports are supported at this time"})
  }
  else if (!/^\d{4}$/.test(reportYear)) {
    errors.push({file, msg: "Report Year: " + reportYear + " is malformed"})
  }
  else if (!/^\w{3,4}$/.test(fileExt)) {
    errors.push({file, msg: "File Extension: " + fileExt + " is malformed"})
  }
  else if (!/^\d{5}[A-Z]? - [A-Z]{2}$/.test(fundNumber)) {
    errors.push({file, msg: "Invalid Fund Number: " + fundNumber + ', for file:'})
  }
}
